import { useNavigate } from "react-router-dom";
import { ChevronLeft, Shield } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: [
      "When you use Exqura, we may collect the following information:",
      "• Account Information: Name, email address, and profile photo provided during sign-up or via Facebook Login.",
      "• Profile Data: Full name, golf handicap, passport details (nationality, passport number, expiry date), and profile photo you voluntarily provide.",
      "• Booking Data: Golf course selections, travel dates, ferry and transport preferences, accommodation choices, and number of passengers.",
      "• Device & Usage Data: IP address, browser type, pages visited, and timestamps — collected automatically to improve our service.",
      "• Facebook Data: If you sign in via Facebook, we receive your public profile (name, profile picture, email) as permitted by your Facebook privacy settings.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "We use the information we collect to:",
      "• Create and manage your Exqura account.",
      "• Process and confirm golf, ferry, transport, and accommodation bookings.",
      "• Send booking confirmations, updates, and service notifications.",
      "• Personalise your experience and improve our platform.",
      "• Comply with legal obligations and prevent fraudulent activity.",
      "We do not sell your personal data to third parties.",
    ],
  },
  {
    title: "3. Facebook Login & Permissions",
    body: [
      "Exqura uses Facebook Login as an optional sign-in method. When you choose to log in with Facebook, we request only the minimum permissions required:",
      "• public_profile — to display your name and profile picture.",
      "• email — to create and identify your Exqura account.",
      "We do not post to your Facebook timeline, access your friends list, or request any additional permissions beyond the above.",
    ],
  },
  {
    title: "4. Data Sharing",
    body: [
      "We may share your information with:",
      "• Service Providers: Third-party vendors (e.g. Firebase / Google Cloud) who process data on our behalf under strict confidentiality agreements.",
      "• Booking Partners: Golf courses, ferry operators, transport providers, and accommodation suppliers — only the booking-relevant data required to fulfil your reservation.",
      "• Legal Authorities: When required by law, court order, or to protect the rights and safety of Exqura and its users.",
      "We do not share your data with advertisers or data brokers.",
    ],
  },
  {
    title: "5. Data Retention",
    body: [
      "We retain your personal data for as long as your account is active or as needed to provide services.",
      "If you request account deletion, we will delete or anonymise your personal data within 30 days, except where retention is required by law (e.g. financial records).",
    ],
  },
  {
    title: "6. Your Rights",
    body: [
      "You have the right to:",
      "• Access the personal data we hold about you.",
      "• Correct inaccurate or incomplete data.",
      "• Request deletion of your personal data (see our Data Deletion page).",
      "• Withdraw consent for optional data processing at any time.",
      "• Lodge a complaint with a relevant data protection authority.",
      "To exercise these rights, email us at privacy@exqura.com.",
    ],
  },
  {
    title: "7. Cookies",
    body: [
      "Exqura uses cookies and similar technologies to maintain your session, remember preferences, and analyse usage. You can control cookie behaviour through your browser settings, though disabling certain cookies may affect app functionality.",
    ],
  },
  {
    title: "8. Security",
    body: [
      "We implement industry-standard security measures including encrypted data transmission (HTTPS/TLS), Firebase Authentication, and access controls to protect your information. No method of transmission over the internet is 100% secure; we encourage you to use a strong password and keep your login credentials confidential.",
    ],
  },
  {
    title: "9. Children's Privacy",
    body: [
      "Exqura is not directed at children under the age of 13. We do not knowingly collect personal data from children. If you believe a child has provided us with personal information, please contact us immediately.",
    ],
  },
  {
    title: "10. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. We will notify you of material changes by posting the revised policy on this page with an updated effective date. Continued use of Exqura after changes constitutes your acceptance of the updated policy.",
    ],
  },
  {
    title: "11. Contact Us",
    body: [
      "If you have questions about this Privacy Policy or how we handle your data, please contact us:",
      "Email: privacy@exqura.com",
      "Website: www.exqura.com",
      "Exqura Golf & Experiences",
      "Singapore",
    ],
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0B2421]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest">Legal</p>
          </div>
          <h1 className="text-white font-bold text-3xl sm:text-4xl mb-2">Privacy Policy</h1>
          <p className="text-white/50 text-sm">Effective date: 9 July 2026 &nbsp;·&nbsp; Last updated: 9 July 2026</p>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-6">
          <p className="text-gray-600 leading-relaxed">
            Welcome to <span className="font-semibold text-[#0B2421]">Exqura Golf & Experiences</span> ("Exqura", "we", "our", or "us").
            This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our website and mobile application
            (collectively, the "Service"). By using Exqura, you agree to the practices described in this policy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <h2 className="text-[#0B2421] font-bold text-lg mb-4">{section.title}</h2>
              <div className="space-y-2">
                {section.body.map((para, i) => (
                  <p key={i} className="text-gray-600 text-sm leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs mt-8 pb-8">
          © {new Date().getFullYear()} Exqura Golf & Experiences. All rights reserved.
        </p>
      </div>
    </div>
  );
}
